import {
  Alert,
  Collapse,
  DatePicker,
  Row,
  Col,
  Switch,
  Input,
  Typography,
  InputNumber,
  Form,
  Image
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { isAuthenticated } from "../../../commons/Auth/auth";
import { Error, Success, Warning } from "../../../commons/Messages/Messages";
import SelectComp from "../../../commons/Select/Select";
import { SocketContext } from "../../../context/SocketContext";
import "./Employees.css";
import GeneralModal from "../General.modal";
import UploadImageNew from "../../../commons/UploadImage/UploadImageNew";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { useTranslation } from "react-i18next";
import { dayFormat } from "../../../utils/dayjs";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import InlineNetPreview from "../../../commons/Payroll/InlineNetPreview";
import { BUNDESLAENDER, STEUERKLASSEN } from "../../../commons/Payroll/payrollOptions";

const { Title } = Typography;

export const days = [
  { label: "Monday", value: "monday" },
  { label: "Tuesday", value: "tuesday" },
  { label: "Wednesday", value: "wednesday" },
  { label: "Thursday", value: "thursday" },
  { label: "Friday", value: "friday" },
  { label: "Saturday", value: "saturday" },
  { label: "Sunday", value: "sunday" }
];

const initialFormData = {
  picture: "",
  firstName: "",
  lastName: "",
  username: "",
  password: "",
  confirm: "",
  employeeId: "",
  phone: "",
  joiningDate: dayjs(new Date()).format(dayFormat),
  birthDate: dayjs(new Date()).format(dayFormat),
  department: null,
  designation: null,
  maritalStatus: "",
  numberOfKids: 0,
  canLogin: false,
  contract: {
    startDate: null,
    endDate: null,
    salary: null,
    workSchedule: "hourly",
    workingDays: [],
    vacationsDays: [],
    vacationsYear: null,
    note: null
  },
  // Lohnsteuerabzugsmerkmale; only `steuerklasse` has to be filled in, the backend defaults
  // the rest to what applies to most employments.
  payroll: {
    steuerklasse: null,
    kinderfreibetraege: 0,
    churchTax: false,
    bundesland: null,
    zusatzbeitrag: null
  },
  contractEmail: "",
  contractPhone: "",
  street: "",
  streetNumber: "",
  zipCode: "",
  city: "",
  state: "",
  country: ""
};

/**
 * The payroll data that is only needed for the statutory filings or for employees with extra
 * pay components. Kept behind a disclosure so the common case stays a gross and a Steuerklasse.
 */
const PayrollDetailFields = () => {
  const { t } = useTranslation("dashboard");
  const field = (name, label, control, hint) => (
    <div className="col-md-6">
      <div className="input-block">
        <Title level={5} className="modals-inputs-label">
          {label}
        </Title>
        <Form.Item name={name} extra={hint}>
          {control}
        </Form.Item>
      </div>
    </div>
  );

  return (
    <div className="row gy-3">
      {field(
        ["payroll", "steuerId"],
        t("Steuer-Identifikationsnummer"),
        <Input placeholder={t("11 digits")} maxLength={11} />,
        t("Required on the Lohnsteuerbescheinigung.")
      )}
      {field(
        ["payroll", "sozialversicherungsnummer"],
        t("Sozialversicherungsnummer"),
        <Input placeholder={t("e.g. 65170839J003")} />,
        t("Required for every DEÜV message.")
      )}
      {field(
        ["payroll", "religion"],
        t("Religionsschlüssel"),
        <SelectComp
          options={[
            { value: "", label: t("None") },
            { value: "ev", label: t("ev — evangelisch") },
            { value: "rk", label: t("rk — römisch-katholisch") },
            { value: "ak", label: t("ak — altkatholisch") }
          ]}
        />,
        t("The Lohnsteuer-Anmeldung reports church tax per denomination.")
      )}
      {field(
        ["payroll", "krankenkasse", "name"],
        t("Krankenkasse"),
        <Input placeholder={t("e.g. AOK Rheinland")} />
      )}
      {field(
        ["payroll", "krankenkasse", "betriebsnummer"],
        t("Betriebsnummer der Krankenkasse"),
        <Input placeholder={t("8 digits")} maxLength={8} />,
        t("Recipient of the Beitragsnachweis.")
      )}
      {field(
        ["payroll", "regularWeeklyHours"],
        t("Regular weekly hours"),
        <InputNumber min={0} max={60} step={0.5} style={{ width: "100%" }} />,
        t("Used to derive the hourly Grundlohn of a monthly salary.")
      )}
      {field(
        ["payroll", "umlageU1Rate"],
        t("Umlage U1 (%)"),
        <InputNumber min={0} max={10} step={0.1} style={{ width: "100%" }} />,
        t("From the Krankenkasse; only for employers with up to 30 employees.")
      )}
      {field(
        ["payroll", "umlageU2Rate"],
        t("Umlage U2 (%)"),
        <InputNumber min={0} max={10} step={0.01} style={{ width: "100%" }} />,
        t("From the Krankenkasse; applies to every employer.")
      )}
      {field(
        ["payroll", "bavEntgeltumwandlung"],
        t("Entgeltumwandlung BAV (€/month)"),
        <InputNumber min={0} step={10} style={{ width: "100%" }} />,
        t("Tax-free up to 8 % and contribution-free up to 4 % of the pension ceiling.")
      )}
      {field(
        ["payroll", "vermoegenswirksameLeistungen"],
        t("Vermögenswirksame Leistungen (€/month)"),
        <InputNumber min={0} step={5} style={{ width: "100%" }} />,
        t("Fully taxable and contributory, paid to the savings contract.")
      )}
      {field(
        ["payroll", "sachbezuege"],
        t("Geldwerter Vorteil (€/month)"),
        <InputNumber min={0} step={10} style={{ width: "100%" }} />,
        t("Company car, meals and the like. Taxed, but not paid out.")
      )}
      {field(
        ["payroll", "steuerfreieBezuege"],
        t("Tax-free allowance (€/month)"),
        <InputNumber min={0} step={10} style={{ width: "100%" }} />,
        t("Free of tax and contributions, paid on top.")
      )}
      <div className="col-md-6">
        <div className="input-block">
          <Title level={5} className="modals-inputs-label">
            {t("Pays § 3b shift surcharges")}
          </Title>
          <Form.Item
            name={["payroll", "zuschlaegeEnabled"]}
            valuePropName="checked"
            extra={t(
              "Only switch this on where the contract actually provides for Sunday, holiday or night surcharges — § 3b exempts only surcharges that are really paid."
            )}
          >
            <Switch />
          </Form.Item>
        </div>
      </div>
    </div>
  );
};

/**
 * Watches the payroll fields of the employee form and previews what the employee takes home.
 * Hourly employees have no fixed gross per period, so there is nothing to preview for them.
 */
const PayrollPreview = ({ form, employeeId }) => {
  const { t } = useTranslation("dashboard");
  const workSchedule = Form.useWatch(["contract", "workSchedule"], form);
  const salary = Form.useWatch(["contract", "salary"], form);
  const payroll = Form.useWatch("payroll", form);
  const numberOfKids = Form.useWatch("numberOfKids", form);

  if (workSchedule !== "monthly") {
    return (
      <Alert
        type="info"
        showIcon
        message={t(
          "Set the pay type to a monthly gross salary to preview the Lohnsteuer here. Hourly employees are calculated per salary slip from the hours worked."
        )}
      />
    );
  }

  return (
    <InlineNetPreview
      employeeId={employeeId}
      values={{
        brutto: salary,
        period: "month",
        steuerklasse: payroll?.steuerklasse,
        kinderfreibetraege: payroll?.kinderfreibetraege,
        churchTax: payroll?.churchTax,
        bundesland: payroll?.bundesland,
        zusatzbeitrag: payroll?.zusatzbeitrag,
        children: numberOfKids
      }}
    />
  );
};

const AddEditEmployees = ({ data, updateParent, isCreate = false }) => {
  const socket = useContext(SocketContext);
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [canLogin, setCanLogin] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);

  const getDesignations = async id => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/designations/dep`,
        { id },
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") }
        }
      );
      if (res.status === 200 || res.status === 304) {
        let makeOptions = res.data?.map(d => ({ label: d?.title, value: d?._id }));
        setDesignations(makeOptions);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const getEmployee = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/employees/employee/${data._id}`,
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") }
        }
      );

      if (res.status === 200 || res.status === 304) {
        const emp = res.data;
        const contract = {
          startDate: emp?.startDate ?? emp?.contract?.startDate ?? null,
          endDate: emp?.endDate ?? emp?.contract?.endDate ?? null,
          workSchedule: emp?.workSchedule ?? emp?.contract?.workSchedule ?? "hourly",
          salary: emp?.salary ?? emp?.contract?.salary ?? null,
          workingDays: emp?.workingDays ?? emp?.contract?.workingDays ?? [],
          vacationsYear: emp?.vacationsYear ?? emp?.contract?.vacationsYear ?? null,
          vacationsDays: emp?.vacationsDays ?? emp?.contract?.vacationsDays ?? [],
          note: emp?.note ?? emp?.contract?.note ?? null
        };

        const newFormData = {
          ...initialFormData,
          ...emp,
          contract
        };
        setFormData(newFormData);

        let canLogin = emp.username != null && emp.username != "";

        const fieldsToSet = {
          ...emp,
          department: emp?.department?._id || emp?.department,
          designation: emp?.designation?._id || emp?.designation,
          joiningDate: emp?.joiningDate ? dayjs(emp.joiningDate, dayFormat) : null,
          birthDate: emp?.birthDate ? dayjs(emp.birthDate, dayFormat) : null,
          canLogin,
          contract: {
            ...contract,
            startDate: contract.startDate ? dayjs(contract.startDate) : null,
            endDate: contract.endDate ? dayjs(contract.endDate) : null
          }
        };

        setCanLogin(canLogin);
        setChangePassword(false);

        form.setFieldsValue(fieldsToSet);

        if (emp.picture) {
          form.setFieldValue("picture", emp.picture);
        }
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const getDepartments = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/departments`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      if (res.status === 200 || res.status === 304) {
        let makeOptions = res.data?.map(d => ({ label: d?.title, value: d?._id }));
        setDepartments(makeOptions);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      getDesignations(data?.department);
      getDepartments();
      if (!isCreate) {
        getEmployee();
      } else {
        setFormData({ ...initialFormData });
        setCanLogin(false);
        setChangePassword(false);
        form.resetFields();
        form.setFieldsValue({
          joiningDate: dayjs(new Date()),
          birthDate: dayjs(new Date()),
          contract: { workSchedule: "hourly" }
        });
      }
    } else {
      setFormData({ ...initialFormData });
      setCanLogin(false);
      setChangePassword(false);
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => setIsModalOpen(false);

  const handleChange = (key, val) => {
    setFormData(prev => ({ ...prev, [key]: val }));
  };

  const handleContractChange = (key, val) => {
    setFormData(prev => ({
      ...prev,
      contract: { ...prev.contract, [key]: val }
    }));
  };

  const onFinish = async values => {
    try {
      const picture = form.getFieldValue("picture");
      const payload = {
        ...formData,
        ...values,
        canLogin,
        picture: picture ?? formData.picture
      };

      if (payload.joiningDate?.format) payload.joiningDate = payload.joiningDate.format(dayFormat);
      if (payload.birthDate?.format) payload.birthDate = payload.birthDate.format(dayFormat);
      if (payload.contract) {
        if (payload.contract.startDate?.format)
          payload.contract.startDate = payload.contract.startDate.format("DD/MMM/YYYY");
        if (payload.contract.endDate?.format)
          payload.contract.endDate = payload.contract.endDate.format("DD/MMM/YYYY");
      }

      if (!isCreate && !changePassword) {
        delete payload.password;
        delete payload.confirm;
      }

      if (isCreate) {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/employees/signup`,
          payload,
          { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
        );
        if (res.status === 200 || res.status === 304) {
          Success(res.data.successMessage);
          updateParent();
          handleCancel();
          socket.emit("refresh service provider data", {
            user: isAuthenticated()?.serviceProviderId
          });
        } else if (res.status === 211) {
          Warning(res.data.errorMessage);
        } else {
          Error(res.data.errorMessage);
        }
      } else {
        const res = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/employees/update/${data?._id}`,
          payload,
          { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
        );
        if (res.status === 200 || res.status === 304) {
          Success(res.data.successMessage);
          updateParent();
          handleCancel();
          socket.emit("refresh service provider data", {
            user: isAuthenticated()?.serviceProviderId
          });
        } else {
          Error(res.data.errorMessage);
        }
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const getBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const handleChangeimg = info => {
    if (info.file.status === "uploading") {
      setLoading(true);
      handleChange("picture", info.file?.response);
      return;
    }
    if (info.file.status === "done") {
      getBase64(info.file.originFileObj, url => {
        setLoading(false);
        setPreviewImage(url);
      });
    }
  };

  const handlePreview = async file => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const uploadButton = (
    <button className="employee-upload-button" type="button">
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div className="employee-upload-button__label">{t("Upload pic")}</div>
    </button>
  );

  return (
    <div className="AddEmployees">
      {isCreate ? (
        <MainButtonComp text="Add Employees" addBtn onClick={showModal} />
      ) : (
        <MainButtonComp text="Edit" editBtn onClick={showModal} />
      )}

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <GeneralModal
          title={<Title level={4}>{isCreate ? t("Add Employee") : t("Update Employee")}</Title>}
          onOk={() => form.submit()}
          open={isModalOpen}
          okText={t("Save")}
          onCancel={handleCancel}
          items={
            <>
              <Row gutter={[16, 16]}>
                {/* ── Profile Picture ── */}
                <Col xs={24} md={6}>
                  <Form.Item name="picture" valuePropName="fileList" getValueFromEvent={e => e}>
                    <UploadImageNew name="picture" listType="picture-circle" maxCount={1}>
                      {formData?.picture?.url ? (
                        <img
                          className="rounded-circle"
                          src={formData?.picture?.url}
                          alt="User"
                          style={{ width: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        uploadButton
                      )}
                    </UploadImageNew>
                  </Form.Item>

                  {previewImage && (
                    <Image
                      wrapperStyle={{ display: "none" }}
                      preview={{
                        visible: previewOpen,
                        onVisibleChange: visible => setPreviewOpen(visible),
                        afterOpenChange: visible => !visible && setPreviewImage("")
                      }}
                      src={previewImage}
                    />
                  )}
                </Col>

                {/* ── Personal Information ── */}
                <Col xs={24} md={18}>
                  <div className="row gy-4 mt-0">
                    {/* First Name */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("First Name")} <span className="text-danger">*</span>
                        </Title>
                        <Form.Item
                          name="firstName"
                          rules={[{ required: true, message: t("Please enter first name") }]}
                        >
                          <Input
                            placeholder={t("First Name")}
                            onChange={e => handleChange("firstName", e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Last Name */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Last Name")} <span className="text-danger">*</span>
                        </Title>
                        <Form.Item
                          name="lastName"
                          rules={[{ required: true, message: t("Please enter last name") }]}
                        >
                          <Input
                            placeholder={t("Last Name")}
                            onChange={e => handleChange("lastName", e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <label className="col-form-label">
                          {t("Email")} {canLogin ? <span className="text-danger">*</span> : ""}
                        </label>
                        <Form.Item
                          name="email"
                          rules={[
                            {
                              required: canLogin,
                              type: "email",
                              message: t("Please enter a valid email")
                            }
                          ]}
                        >
                          <Input
                            type="email"
                            placeholder={t("Email")}
                            onChange={e => handleChange("email", e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Phone")}
                        </Title>
                        <Form.Item name="phone">
                          <Input
                            placeholder={t("Enter phone number")}
                            onChange={e => {
                              handleChange("phone", e.target.value);
                              form.setFieldsValue({ phone: e.target.value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Department */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Department")}
                        </Title>
                        <Form.Item name="department">
                          <SelectComp
                            placeholder={t("Select Department")}
                            options={departments}
                            handleChange={val => {
                              handleChange("department", val);
                              form.setFieldsValue({ department: val, designation: null });
                              getDesignations(val);
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Designation */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Designation")}
                        </Title>
                        <Form.Item name="designation">
                          <SelectComp
                            placeholder={t("Select Designation")}
                            options={designations}
                            handleChange={val => handleChange("designation", val)}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Marital Status */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Marital Status")}
                        </Title>
                        <Form.Item name="maritalStatus">
                          <SelectComp
                            placeholder={t("Select Marital Status")}
                            options={[
                              { label: t("Single"), value: "single" },
                              { label: t("Married"), value: "married" },
                              { label: t("Divorced"), value: "divorced" },
                              { label: t("Widowed"), value: "widowed" }
                            ]}
                            handleChange={val => handleChange("maritalStatus", val)}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Number Of Kids */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Number Of Kids")}
                        </Title>
                        <Form.Item name="numberOfKids">
                          <InputNumber
                            min={0}
                            placeholder={t("Enter number of kids")}
                            style={{ width: "100%" }}
                            onChange={val => {
                              handleChange("numberOfKids", val);
                              form.setFieldsValue({ numberOfKids: val });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Birth Date */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Birth Date")}
                        </Title>
                        <Form.Item name="birthDate">
                          <DatePicker
                            format={dayFormat}
                            placeholder={t("Select Birth Date")}
                            style={{ width: "100%" }}
                            onChange={date => {
                              handleChange("birthDate", date);
                              form.setFieldsValue({ birthDate: date });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Joining Date */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Joining Date")}
                        </Title>
                        <Form.Item name="joiningDate">
                          <DatePicker
                            format={dayFormat}
                            placeholder={t("Select Joining Date")}
                            style={{ width: "100%" }}
                            onChange={date => {
                              handleChange("joiningDate", date);
                              form.setFieldsValue({ joiningDate: date });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* ── Can Login Switch ── */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <Title level={5} className="modals-inputs-label">
                          {t("Can Log In")}
                        </Title>
                        <Form.Item name="canLogin" valuePropName="checked">
                          <Switch
                            checkedChildren={t("Yes")}
                            unCheckedChildren={t("No")}
                            checked={canLogin}
                            onChange={value => {
                              setCanLogin(value);
                              handleChange("canLogin", value);
                              form.setFieldsValue({ canLogin: value });
                              // clear credential fields when disabled
                              if (!value) {
                                form.setFieldsValue({ username: "", password: "", confirm: "" });
                                handleChange("username", "");
                                handleChange("password", "");
                                handleChange("confirm", "");
                              }
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* ── Change Password Switch ── */}
                    {canLogin && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <Title level={5} className="modals-inputs-label">
                            {t("Change Password")}
                          </Title>
                          <Form.Item name="changePassword" valuePropName="checked">
                            <Switch
                              checkedChildren={t("Yes")}
                              unCheckedChildren={t("No")}
                              checked={changePassword}
                              onChange={value => {
                                setChangePassword(value);
                                if (!value) {
                                  form.setFieldsValue({ password: "", confirm: "" });
                                  handleChange("password", "");
                                  handleChange("confirm", "");
                                }
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    )}

                    {/* ── Credentials (shown only when canLogin is true) ── */}
                    {canLogin && (
                      <>
                        {/* Username */}
                        <div className="col-md-6">
                          <div className="input-block">
                            <Title level={5} className="modals-inputs-label">
                              {t("Username")} <span className="text-danger">*</span>
                            </Title>
                            <Form.Item
                              name="username"
                              rules={[{ required: true, message: t("Please enter username") }]}
                            >
                              <Input
                                placeholder={t("Enter username")}
                                onChange={e => {
                                  handleChange("username", e.target.value);
                                  form.setFieldsValue({ username: e.target.value });
                                }}
                              />
                            </Form.Item>
                          </div>
                        </div>

                        {/* Password */}
                        <div className="col-md-6">
                          <div className="input-block">
                            <Title level={5} className="modals-inputs-label">
                              {t("Password")}{" "}
                              {isCreate || changePassword ? (
                                <span className="text-danger">*</span>
                              ) : (
                                ""
                              )}
                            </Title>
                            <Form.Item
                              name="password"
                              rules={[
                                {
                                  required: isCreate || changePassword,
                                  message: t("Please input password")
                                },
                                { min: 6, message: t("Password must be at least 6 characters") }
                              ]}
                              hasFeedback
                            >
                              <Input
                                type="password"
                                placeholder={t("Password")}
                                onChange={e => handleChange("password", e.target.value)}
                              />
                            </Form.Item>
                          </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="col-md-6">
                          <div className="input-block">
                            <Title level={5} className="modals-inputs-label">
                              {t("Confirm Password")}{" "}
                              {isCreate || changePassword ? (
                                <span className="text-danger">*</span>
                              ) : (
                                ""
                              )}
                            </Title>
                            <Form.Item
                              name="confirm"
                              dependencies={["password"]}
                              hasFeedback
                              rules={[
                                {
                                  required: isCreate || changePassword,
                                  message: t("Please confirm password")
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    if (!value || getFieldValue("password") === value) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error(t("The two passwords do not match!"))
                                    );
                                  }
                                })
                              ]}
                            >
                              <Input
                                type="password"
                                placeholder={t("Confirm Password")}
                                onChange={e => handleChange("confirm", e.target.value)}
                              />
                            </Form.Item>
                          </div>
                        </div>
                      </>
                    )}

                    {/* ── Payroll / Lohnsteuer ── */}
                    <div className="col-12">
                      <Title level={5} className="modals-inputs-label">
                        {t("Payroll (Lohnsteuer)")}
                      </Title>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Pay type")}
                        </Title>
                        <Form.Item name={["contract", "workSchedule"]}>
                          <SelectComp
                            options={[
                              { value: "monthly", label: t("Monthly gross salary") },
                              { value: "hourly", label: t("Hourly rate") }
                            ]}
                            handleChange={val => handleContractChange("workSchedule", val)}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Gross salary (€)")}
                        </Title>
                        <Form.Item name={["contract", "salary"]}>
                          <InputNumber
                            min={0}
                            step={100}
                            style={{ width: "100%" }}
                            placeholder={t("Brutto")}
                            onChange={val => handleContractChange("salary", val)}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Steuerklasse")}
                        </Title>
                        <Form.Item name={["payroll", "steuerklasse"]}>
                          <SelectComp
                            options={STEUERKLASSEN.map(o => ({ ...o, label: t(o.label) }))}
                            placeholder={t("I–VI")}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Bundesland")}
                        </Title>
                        <Form.Item name={["payroll", "bundesland"]}>
                          <SelectComp options={BUNDESLAENDER} placeholder={t("Select")} />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Kinderfreibeträge")}
                        </Title>
                        <Form.Item name={["payroll", "kinderfreibetraege"]}>
                          <InputNumber min={0} max={20} step={0.5} style={{ width: "100%" }} />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Zusatzbeitrag (%)")}
                        </Title>
                        <Form.Item name={["payroll", "zusatzbeitrag"]}>
                          <InputNumber
                            min={0}
                            max={10}
                            step={0.1}
                            style={{ width: "100%" }}
                            placeholder={t("2.9 (average)")}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Liable for Kirchensteuer")}
                        </Title>
                        <Form.Item name={["payroll", "churchTax"]} valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-12">
                      <PayrollPreview form={form} employeeId={data?._id} />
                    </div>

                    <div className="col-12">
                      <Collapse
                        ghost
                        items={[
                          {
                            key: "payroll-details",
                            label: t("Payroll identifiers, pay components and surcharges"),
                            children: <PayrollDetailFields />
                          }
                        ]}
                      />
                    </div>

                    {/* ── Address ── */}
                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Street")}
                        </Title>
                        <Form.Item
                          name="street"
                          // rules={[{ required: true, message: t("Please enter street") }]}
                        >
                          <Input
                            placeholder={t("Enter street name")}
                            onChange={e => {
                              handleChange("street", e.target.value);
                              form.setFieldsValue({ street: e.target.value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Street Number")}
                        </Title>
                        <Form.Item
                          name="streetNumber"
                          // rules={[{ required: true, message: t("Please enter street number") }]}
                        >
                          <Input
                            type="number"
                            placeholder={t("Enter street number")}
                            onChange={e => {
                              handleChange("streetNumber", e.target.value);
                              form.setFieldsValue({ streetNumber: e.target.value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Postal Code")}
                        </Title>
                        <Form.Item
                          name="zipCode"
                          // rules={[{ required: true, message: t("Please enter postal code") }]}
                        >
                          <Input
                            type="number"
                            placeholder={t("Enter postal code")}
                            onChange={e => {
                              handleChange("zipCode", e.target.value);
                              form.setFieldsValue({ zipCode: e.target.value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("City")}
                        </Title>
                        <Form.Item
                          name="city"
                          // rules={[{ required: true, message: t("Please enter city") }]}
                        >
                          <Input
                            placeholder={t("Enter city")}
                            onChange={e => {
                              handleChange("city", e.target.value);
                              form.setFieldsValue({ city: e.target.value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("State")}
                        </Title>
                        <Form.Item
                          name="state"
                          // rules={[{ required: true, message: t("Please enter state") }]}
                        >
                          <Input
                            placeholder={t("Enter state")}
                            onChange={e => {
                              handleChange("state", e.target.value);
                              form.setFieldsValue({ state: e.target.value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-block">
                        <Title level={5} className="modals-inputs-label">
                          {t("Country")}
                        </Title>
                        <Form.Item
                          name="country"
                          // rules={[{ required: true, message: t("Please enter country") }]}
                        >
                          <Input
                            placeholder={t("Enter country")}
                            onChange={e => {
                              handleChange("country", e.target.value);
                              form.setFieldsValue({ country: e.target.value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          }
        />
      </Form>
    </div>
  );
};

export default AddEditEmployees;
